import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api, { setSession, getToken } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Loader2, Lock, Mail, Phone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4a2dee35-189a-4f59-95bd-a089560b8efb/artifacts/rzc72v1x_thara%20%281%29.png";

export default function Login() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");   // email OR mobile
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (getToken()) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const id = identifier.trim();
      // Send BOTH keys to the API and let the backend pick the right path.
      const isEmail = id.includes("@");
      const payload = { password };
      if (isEmail) payload.email = id;
      else payload.mobile = id.replace(/[\s-]/g, "");
      const res = await api.post("/auth/login", payload);
      setSession(res.data.token, res.data.user);
      toast.success(`Welcome back, ${res.data.user.name}`);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    // h-screen + overflow-hidden guarantees the two columns share ONE viewport
    // (never stack vertically). Internal columns can scroll if content overflows.
    <div className="h-screen w-screen overflow-hidden flex bg-white" data-testid="login-page">
      <Toaster richColors position="top-right" />

      {/* Left: Brand banner (hidden below md — small phones show form only) */}
      <div
        className="hidden md:flex relative overflow-hidden items-center justify-center
                   md:w-1/2 lg:w-1/2 xl:w-[55%]
                   bg-gradient-to-br from-[#9B1C1C] via-[#7F1D1D] to-[#451A1A]"
        data-testid="login-banner-left"
      >
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }} />
        <div className="absolute top-10 right-10 w-56 h-56 lg:w-72 lg:h-72 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-48 h-48 lg:w-64 lg:h-64 rounded-full bg-emerald-500/20 blur-3xl" />

        <div className="relative z-10 max-w-md text-white px-6 lg:px-8 py-8 space-y-6 lg:space-y-8 overflow-y-auto max-h-full">
          <div className="bg-white rounded-lg p-5 lg:p-6 inline-flex">
            <img src={LOGO_URL} alt="Thara Gold" className="h-16 lg:h-20 w-auto object-contain" />
          </div>
          <div className="space-y-3 lg:space-y-4">
            <h2 className="font-heading text-2xl lg:text-3xl xl:text-4xl font-bold leading-tight">
              Smart inventory.<br />Smarter procurement.
            </h2>
            <p className="text-sm lg:text-base text-white/80 leading-relaxed">
              Real-time sync with Wings ERP across all branches. Built for the imitation jewellery wholesale and retail business.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 lg:gap-4 pt-3 lg:pt-4 border-t border-white/20">
            <div>
              <p className="font-heading text-xl lg:text-2xl font-bold text-amber-300">17</p>
              <p className="text-[10px] lg:text-xs uppercase tracking-widest text-white/60">Branches</p>
            </div>
            <div>
              <p className="font-heading text-xl lg:text-2xl font-bold text-amber-300">80k+</p>
              <p className="text-[10px] lg:text-xs uppercase tracking-widest text-white/60">Products</p>
            </div>
            <div>
              <p className="font-heading text-xl lg:text-2xl font-bold text-amber-300">15m</p>
              <p className="text-[10px] lg:text-xs uppercase tracking-widest text-white/60">Sync</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Form (full width below md, half width from md up) */}
      <div className="flex-1 md:w-1/2 lg:w-1/2 xl:w-[45%] flex items-center justify-center px-6 sm:px-8 py-6 overflow-y-auto"
           data-testid="login-form-column">
        <div className="w-full max-w-md space-y-6">
          <div className="flex items-center gap-3 md:hidden">
            {/* Mobile-only logo since brand banner is hidden below md */}
            <img src={LOGO_URL} alt="Thara Gold" className="h-12 w-auto object-contain" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">Inventory Suite</p>
              <p className="font-heading text-base font-semibold text-gray-900">Thara Gold</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <h1 className="font-heading text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tight text-gray-900">
              Welcome back.
            </h1>
            <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
              Sign in to manage stock, sync with Wings ERP, and plan smarter procurement across all branches.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div className="space-y-1.5">
              <Label htmlFor="identifier" className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                Email or Mobile
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" strokeWidth={1.5} />
                <Input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="pl-10 h-11"
                  placeholder="you@example.com or 9876543210"
                  autoComplete="off"
                  data-testid={AUTH.emailInput}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" strokeWidth={1.5} />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11"
                  autoComplete="new-password"
                  data-testid={AUTH.passwordInput}
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid={AUTH.submitButton}
              className="w-full h-11 bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white font-medium"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <p className="text-[11px] text-gray-500">
            © {new Date().getFullYear()} Thara Gold Covering.
          </p>
        </div>
      </div>
    </div>
  );
}
